// TTS Profile Management Module
// Handles CRUD operations for TTS profiles (ElevenLabs, Piper, OpenAI, Azure)

import { CONFIG } from '../../config.js';

const API_BASE = CONFIG.WS_API_BASE;

// Helper function to escape HTML
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
}

// Show status message in TTS profile form
function showTTSProfileStatus(message, type) {
    const ttsProfileStatusMessage = document.getElementById('ttsProfileStatusMessage');
    if (ttsProfileStatusMessage) {
        ttsProfileStatusMessage.textContent = message;
        ttsProfileStatusMessage.className = `status-message ${type}`;
        if (type === 'success') {
            setTimeout(() => {
                ttsProfileStatusMessage.className = 'status-message';
            }, 3000);
        }
    }
}

// Fetch all TTS profiles
export async function fetchTTSProfiles() {
    const ttsProfileList = document.getElementById('ttsProfileList');

    try {
        // Fetch all profiles and active profile in parallel
        const [profilesResponse, activeResponse] = await Promise.all([
            fetch(`${API_BASE}/tts-profiles`),
            fetch(`${API_BASE}/tts-profiles/active`)
        ]);

        if (!profilesResponse.ok) throw new Error(`HTTP ${profilesResponse.status}`);
        const profiles = await profilesResponse.json();

        // Get active profile ID
        let activeProfileId = null;
        if (activeResponse.ok) {
            const activeProfile = await activeResponse.json();
            activeProfileId = activeProfile.id;
        }

        // Mark the active profile
        profiles.forEach(p => {
            p.is_active = (p.id === activeProfileId);
        });

        renderTTSProfiles(profiles);
    } catch (error) {
        if (ttsProfileList) ttsProfileList.innerHTML = `<div class="empty-state">${window.i18n?.t('settings.ttsProfiles.loadError') || 'Error loading'}: ${error.message}</div>`;
    }
}

// Render TTS profiles list
function renderTTSProfiles(profiles) {
    const ttsProfileList = document.getElementById('ttsProfileList');

    if (!profiles || profiles.length === 0) {
        ttsProfileList.innerHTML = `<div class="empty-state">${window.i18n?.t('settings.ttsProfiles.emptyState') || 'No TTS profiles available'}</div>`;
        return;
    }

    // Ensure at least one profile is active (default to first if none)
    const hasActiveProfile = profiles.some(p => p.is_active);
    if (!hasActiveProfile && profiles.length > 0) {
        profiles[0].is_active = true;
    }

    ttsProfileList.innerHTML = profiles.map(profile => `
        <div class="profile-card ${profile.is_active ? 'active' : ''}" data-id="${profile.id}">
            <div class="profile-card-header">
                <span class="profile-name">
                    ${profile.is_active ? `<span class="active-label">✓ ${window.i18n?.t('common.active') || 'Active'}</span>` : ''}
                    ${escapeHtml(profile.name)}
                </span>
                <div class="profile-actions">
                    ${!profile.is_active ? `<button class="profile-btn success" onclick="activateTTSProfile(${profile.id})">${window.i18n?.t('common.activate') || 'Activate'}</button>` : `<span class="active-indicator">${window.i18n?.t('common.inUse') || 'In Use'}</span>`}
                    <button class="profile-btn" onclick="editTTSProfile(${profile.id})">${window.i18n?.t('common.edit') || 'Edit'}</button>
                    <button class="profile-btn danger" onclick="deleteTTSProfile(${profile.id}, '${escapeHtml(profile.name)}')">${window.i18n?.t('common.delete') || 'Delete'}</button>
                </div>
            </div>
            <div class="profile-model">
                <span style="background: #333; padding: 2px 6px; border-radius: 4px; font-size: 0.8em; margin-right: 5px;">${escapeHtml(profile.provider_type)}</span>
                ${profile.voice_id ? `Voice ID: ${escapeHtml(profile.voice_id)}` : ''}
            </div>
        </div>
    `).join('');
}

// Show TTS profile form for adding
function showTTSProfileForm() {
    const ttsProfileForm = document.getElementById('ttsProfileForm');
    const ttsProfileFormTitle = document.getElementById('ttsProfileFormTitle');
    const addTtsProfileBtn = document.getElementById('addTtsProfileBtn');
    const ttsProfileEditId = document.getElementById('ttsProfileEditId');
    const ttsProfileName = document.getElementById('ttsProfileName');
    const ttsProviderType = document.getElementById('ttsProviderType');
    const ttsApiUrl = document.getElementById('ttsApiUrl');
    const ttsApiKey = document.getElementById('ttsApiKey');
    const ttsModelId = document.getElementById('ttsModelId');
    const ttsVoiceId = document.getElementById('ttsVoiceId');
    const ttsProfileStatusMessage = document.getElementById('ttsProfileStatusMessage');

    ttsProfileFormTitle.textContent = window.i18n?.t('settings.ttsProfiles.newVoice') || 'New Voice';
    ttsProfileEditId.value = '';
    ttsProfileName.value = '';
    ttsProviderType.value = '';
    ttsApiUrl.value = '';
    ttsApiKey.value = '';
    ttsModelId.value = '';
    ttsVoiceId.value = '';
    ttsProfileStatusMessage.className = 'status-message';
    ttsProfileForm.classList.add('show');
    addTtsProfileBtn.style.display = 'none';
}

// Hide TTS profile form
function hideTTSProfileForm() {
    const ttsProfileForm = document.getElementById('ttsProfileForm');
    const addTtsProfileBtn = document.getElementById('addTtsProfileBtn');

    ttsProfileForm.classList.remove('show');
    addTtsProfileBtn.style.display = 'block';
}

// Edit an existing TTS profile
export async function editTTSProfile(id) {
    try {
        const response = await fetch(`${API_BASE}/tts-profiles`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const profiles = await response.json();
        const profile = profiles.find(p => p.id === id);

        if (!profile) {
            alert(window.i18n?.t('settings.llmProfiles.profileNotFound') || 'Profile not found!');
            return;
        }

        const ttsProfileForm = document.getElementById('ttsProfileForm');
        const ttsProfileFormTitle = document.getElementById('ttsProfileFormTitle');
        const addTtsProfileBtn = document.getElementById('addTtsProfileBtn');
        const ttsProfileEditId = document.getElementById('ttsProfileEditId');
        const ttsProfileName = document.getElementById('ttsProfileName');
        const ttsProviderType = document.getElementById('ttsProviderType');
        const ttsApiUrl = document.getElementById('ttsApiUrl');
        const ttsApiKey = document.getElementById('ttsApiKey');
        const ttsModelId = document.getElementById('ttsModelId');
        const ttsVoiceId = document.getElementById('ttsVoiceId');
        const ttsProfileStatusMessage = document.getElementById('ttsProfileStatusMessage');

        ttsProfileFormTitle.textContent = window.i18n?.t('settings.ttsProfiles.editVoice') || 'Edit Voice';
        ttsProfileEditId.value = profile.id;
        ttsProfileName.value = profile.name;
        ttsProviderType.value = profile.provider_type;
        ttsApiUrl.value = profile.api_url || '';
        ttsApiKey.value = profile.api_key || '';
        ttsModelId.value = profile.model_id || '';
        ttsVoiceId.value = profile.voice_id || '';
        ttsProfileStatusMessage.className = 'status-message';
        ttsProfileForm.classList.add('show');
        addTtsProfileBtn.style.display = 'none';
    } catch (error) {
        alert((window.i18n?.t('settings.ttsProfiles.loadError') || 'Error loading profile') + ': ' + error.message);
    }
}

// Save TTS profile (create or update)
async function saveTTSProfile() {
    const ttsProfileName = document.getElementById('ttsProfileName');
    const ttsProviderType = document.getElementById('ttsProviderType');
    const ttsApiUrl = document.getElementById('ttsApiUrl');
    const ttsApiKey = document.getElementById('ttsApiKey');
    const ttsModelId = document.getElementById('ttsModelId');
    const ttsVoiceId = document.getElementById('ttsVoiceId');
    const ttsProfileEditId = document.getElementById('ttsProfileEditId');
    const saveTtsProfileBtn = document.getElementById('saveTtsProfileBtn');

    const name = ttsProfileName.value.trim();
    const providerType = ttsProviderType.value;
    const apiUrl = ttsApiUrl.value.trim();
    const apiKey = ttsApiKey.value.trim();
    const modelId = ttsModelId.value.trim();
    const voiceId = ttsVoiceId.value.trim();
    const editId = ttsProfileEditId.value;

    // Validation
    if (!name) {
        showTTSProfileStatus(window.i18n?.t('settings.ttsProfiles.allFieldsRequired') || 'All fields must be filled!', 'error');
        return;
    }

    if (!providerType) {
        showTTSProfileStatus(window.i18n?.t('settings.ttsProfiles.allFieldsRequired') || 'All fields must be filled!', 'error');
        return;
    }

    if (!apiUrl) {
        showTTSProfileStatus(window.i18n?.t('settings.ttsProfiles.allFieldsRequired') || 'All fields must be filled!', 'error');
        return;
    }

    if (!apiKey) {
        showTTSProfileStatus(window.i18n?.t('settings.ttsProfiles.allFieldsRequired') || 'All fields must be filled!', 'error');
        return;
    }

    if (!modelId) {
        showTTSProfileStatus(window.i18n?.t('settings.ttsProfiles.allFieldsRequired') || 'All fields must be filled!', 'error');
        return;
    }

    saveTtsProfileBtn.disabled = true;
    saveTtsProfileBtn.textContent = window.i18n?.t('common.saving') || 'Saving...';

    try {
        let response;
        const profileData = {
            name: name,
            provider_type: providerType,
            api_url: apiUrl,
            api_key: apiKey,
            model_id: modelId,
            voice_id: voiceId
        };

        if (editId) {
            // Update existing profile - delete and recreate
            await fetch(`${API_BASE}/tts-profiles/${editId}`, { method: 'DELETE' });
            response = await fetch(`${API_BASE}/tts-profiles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(profileData)
            });
        } else {
            // Create new profile
            response = await fetch(`${API_BASE}/tts-profiles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(profileData)
            });
        }

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        showTTSProfileStatus(window.i18n?.t('settings.ttsProfiles.saveSuccess') || '✓ Profile saved successfully!', 'success');
        setTimeout(() => {
            hideTTSProfileForm();
            fetchTTSProfiles();
        }, 800);

    } catch (error) {
        showTTSProfileStatus((window.i18n?.t('settings.ttsProfiles.saveError') || 'Error saving') + ': ' + error.message, 'error');
    } finally {
        saveTtsProfileBtn.disabled = false;
        saveTtsProfileBtn.textContent = window.i18n?.t('common.save') || 'Save';
    }
}

// Activate a TTS profile
export async function activateTTSProfile(id) {
    try {
        const response = await fetch(`${API_BASE}/tts-profiles/${id}/activate`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        fetchTTSProfiles(); // Reload the list
    } catch (error) {
        alert((window.i18n?.t('settings.ttsProfiles.activateError') || 'Error activating') + ': ' + error.message);
    }
}

// Delete a TTS profile
export async function deleteTTSProfile(id, name) {
    if (!confirm(window.i18n?.t('settings.ttsProfiles.deleteConfirm', { name: name }) || `Do you really want to delete profile "${name}"?`)) {
        return;
    }

    try {
        const response = await fetch(`${API_BASE}/tts-profiles/${id}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        fetchTTSProfiles(); // Reload the list
    } catch (error) {
        alert((window.i18n?.t('settings.ttsProfiles.deleteError') || 'Error deleting') + ': ' + error.message);
    }
}

// Initialize TTS profile module
export function initTTSProfiles() {
    const addTtsProfileBtn = document.getElementById('addTtsProfileBtn');
    const cancelTtsProfileBtn = document.getElementById('cancelTtsProfileBtn');
    const saveTtsProfileBtn = document.getElementById('saveTtsProfileBtn');
    const toggleTtsApiKeyBtn = document.getElementById('toggleTtsApiKey');
    const ttsApiKey = document.getElementById('ttsApiKey');

    // Event listeners
    if (addTtsProfileBtn) {
        addTtsProfileBtn.addEventListener('click', showTTSProfileForm);
    }
    if (cancelTtsProfileBtn) {
        cancelTtsProfileBtn.addEventListener('click', hideTTSProfileForm);
    }
    if (saveTtsProfileBtn) {
        saveTtsProfileBtn.addEventListener('click', saveTTSProfile);
    }

    // Toggle TTS API Key visibility
    if (toggleTtsApiKeyBtn && ttsApiKey) {
        toggleTtsApiKeyBtn.addEventListener('click', () => {
            if (ttsApiKey.type === 'password') {
                ttsApiKey.type = 'text';
                toggleTtsApiKeyBtn.textContent = '🙈';
            } else {
                ttsApiKey.type = 'password';
                toggleTtsApiKeyBtn.textContent = '👁️';
            }
        });
    }
}
